import type { ReactNode } from "react";
import { CalendarCheck2, Trophy, Zap } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface LearningGoalsProps {
  todayXp: number;
  lessonsThisWeek: number;
  level: number;
  levelProgress: number;
  xpToNextLevel: number;
  dailyXpGoal?: number;
  weeklyLessonGoal?: number;
}

interface GoalRowProps {
  icon: ReactNode;
  label: string;
  progress: number;
  value: string;
  remaining: string;
  progressClassName: string;
}

const clampProgress = (value: number) => Math.max(0, Math.min(100, value));

function GoalRow({
  icon,
  label,
  progress,
  value,
  remaining,
  progressClassName,
}: GoalRowProps) {
  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="mb-2 flex items-center gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-semibold">{label}</span>
            <span className="flex-shrink-0 text-xs text-muted-foreground">{value}</span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{remaining}</p>
        </div>
      </div>
      <Progress
        value={clampProgress(progress)}
        className={`h-2 ${progressClassName}`}
        aria-label={`${label}: ${value}`}
      />
    </div>
  );
}

export function LearningGoals({
  todayXp,
  lessonsThisWeek,
  level,
  levelProgress,
  xpToNextLevel,
  dailyXpGoal = 100,
  weeklyLessonGoal = 3,
}: LearningGoalsProps) {
  const dailyProgress = dailyXpGoal > 0 ? (todayXp / dailyXpGoal) * 100 : 100;
  const weeklyProgress = weeklyLessonGoal > 0 ? (lessonsThisWeek / weeklyLessonGoal) * 100 : 100;
  const day = new Date().getDay();
  const daysUntilMonday = day === 0 ? 1 : 8 - day;

  return (
    <section className="w-full rounded-xl border border-border/60 bg-card/60 p-4" aria-label="Learning goals">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="font-bold">Learning goals</h2>
        <span className="text-xs text-muted-foreground">
          {daysUntilMonday} {daysUntilMonday === 1 ? "day" : "days"} left
        </span>
      </div>

      <div className="divide-y divide-border/50">
        <GoalRow
          icon={<Zap className="h-4 w-4 text-amber-500" />}
          label="Today's XP"
          progress={dailyProgress}
          value={`${todayXp}/${dailyXpGoal} XP`}
          remaining={todayXp >= dailyXpGoal ? "Daily goal complete" : `${dailyXpGoal - todayXp} XP to finish today's goal`}
          progressClassName="[&>div]:bg-amber-500"
        />
        <GoalRow
          icon={<CalendarCheck2 className="h-4 w-4 text-cyan-500" />}
          label="Weekly lessons"
          progress={weeklyProgress}
          value={`${lessonsThisWeek}/${weeklyLessonGoal}`}
          remaining={lessonsThisWeek >= weeklyLessonGoal ? "Weekly goal complete" : `${weeklyLessonGoal - lessonsThisWeek} lessons to finish this week`}
          progressClassName="[&>div]:bg-cyan-500"
        />
        <GoalRow
          icon={<Trophy className="h-4 w-4 text-emerald-500" />}
          label={`Level ${level + 1}`}
          progress={levelProgress}
          value={`${Math.round(levelProgress)}%`}
          remaining={`${xpToNextLevel} XP to the next level`}
          progressClassName="[&>div]:bg-emerald-500"
        />
      </div>
    </section>
  );
}
